import type { HTMLAttributes, ReactNode } from 'react';

import { Typography } from '@remarkable/ark-web';
import { useLocation } from '@tanstack/react-router';
import clsx from 'clsx';

import type { Link2Props } from 'src/components/Link2';
import { Link2 } from 'src/components/Link2';

export const PillNav = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      className={clsx(
        'flex h-[60px] w-fit flex-row items-center gap-6 rounded-full bg-neutral-light-3 p-8 shadow-lg backdrop-blur-md md:gap-8',
        "[-ms-overflow-style:'none'] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        'mb-32 lm:mb-48',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
};

type PillTabProps = Link2Props & {
  match?: 'exact' | 'prefix';
};

export const PillTab = ({
  to,
  disabled,
  children,
  className,
  match = 'exact',
  ...rest
}: PillTabProps) => {
  const { pathname } = useLocation();
  const active =
    match === 'exact'
      ? pathname === to
      : pathname === to || (to && pathname.startsWith(to + '/'));
  return (
    <Link2
      as="unstyled"
      className={clsx(
        className,
        'rounded-full px-16 py-8 no-underline transition-colors duration-200 hover:no-underline',
        active
          ? 'bg-pill-tertiary/10 text-default hover:bg-neutral-light-6'
          : 'bg-neutral-light-3 text-muted hover:bg-neutral-light-5 hover:text-neutral-dark-5'
      )}
      to={to}
      disabled={disabled}
      aria-disabled={disabled}
      tab-index={disabled ? -1 : undefined}
      data-selected={active}
      {...rest}
    >
      <Typography
        as="span"
        variant="fluid-body-2xs"
        textWrap="nowrap"
        className={clsx('relative', active ? 'font-medium' : 'font-regular')}
      >
        {/* Invisible bold text to reserve space and prevent layout shift */}
        <span aria-hidden="true" className="invisible font-medium">
          {children as ReactNode}
        </span>
        <span className="absolute inset-0 flex items-center justify-center">
          {children as ReactNode}
        </span>
      </Typography>
    </Link2>
  );
};
